<template>
  <div
    :class="$style.root"
    data-testid="language-selector-overlay"
    @click.self="emit('dismiss')"
  >
    <ul :class="$style.list">
      <li
        v-for="(locale, index) of locales"
        :key="locale.code"
        :class="[$style.item, isAnimated[index] && $style.itemAnimated]"
      >
        <button
          type="button"
          :class="$style.link"
          data-testid="language-selector-link"
          @click="emit('select', locale)"
        >
          <img :class="$style.icon" :src="locale.icon" :alt="locale.title" />
          {{ locale.title }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

export interface UiLocale {
  code: string;
  icon: string;
  title: string;
}

interface Props {
  locales: readonly UiLocale[];
  /** Items per row — controls when the staggered animation breaks to a new row delay. */
  flagsPerRow?: number;
  /** Delay (ms) for items in the same row. */
  itemAnimationDurationMs?: number;
  /** Delay (ms) for the first item of each new row (gives a slight pause between rows). */
  rowAnimationDurationMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
  flagsPerRow: 3,
  itemAnimationDurationMs: 110,
  rowAnimationDurationMs: 200,
});

const emit = defineEmits<{
  select: [locale: UiLocale];
  dismiss: [];
}>();

const isAnimated = ref<Record<number, boolean>>({});
const pendingTimers = new Set<ReturnType<typeof setTimeout>>();
let isUnmounting = false;

async function runFlagAnimation(): Promise<void> {
  for (let i = 0; i < props.locales.length; i++) {
    if (isUnmounting) return;
    const duration =
      i % props.flagsPerRow
        ? props.itemAnimationDurationMs
        : props.rowAnimationDurationMs;
    await new Promise<void>((resolve) => {
      const id = setTimeout(() => {
        pendingTimers.delete(id);
        if (!isUnmounting) isAnimated.value[i] = true;
        resolve();
      }, duration);
      pendingTimers.add(id);
    });
  }
}

onMounted(() => {
  isUnmounting = false;
  runFlagAnimation();
});

onBeforeUnmount(() => {
  isUnmounting = true;
  for (const id of pendingTimers) clearTimeout(id);
  pendingTimers.clear();
});
</script>

<style module>
.root {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: var(--kiosk-language-selector-align-items);
  justify-content: center;
  overflow-y: auto;
  mask: var(--kiosk-language-selector-mask);
  padding: var(--kiosk-language-selector-padding);
}

.list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  flex-direction: var(--kiosk-language-selector-list-direction);
  width: var(--kiosk-language-selector-list-width);
  margin: 0;
  padding: 0 0 0 var(--kiosk-language-selector-list-padding-inline-start);
}

.item {
  margin: 0;
  padding: 2.22rem 0;
  width: var(--kiosk-language-selector-item-width);
  opacity: 0;
  transform: translateY(-10%);
  transition: opacity 200ms, transform 200ms;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
}

.itemAnimated {
  opacity: 1;
  transform: none;
}

.link {
  display: flex;
  align-items: center;
  font-size: 190%;
  font-family: var(--Kiosk--font_headers_url);
  white-space: nowrap;
  text-decoration: none;
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  cursor: pointer;
}

:global(.kiosk--capitalize-text) .link {
  text-transform: var(--Kiosk--font_capitalize, uppercase);
}

.icon {
  width: 5.11rem;
  min-width: 5.11rem;
  height: 3.4rem;
  border-radius: 0.33rem;
  box-shadow: 0 1.11rem 0.94rem -0.56rem rgba(0, 0, 0, 0.3);
  display: block;
  margin-right: 1.1rem;
}
</style>
